import {
  Icon,
  List,
  ListRow,
  PageHeader,
  Persona,
  Section,
  SegmentedControl,
  Stack,
  tabbarQuery,
  useMediaQuery,
  type SideNavItem,
} from '@ui';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { useSignOut } from '../features/identity/components/SignOut';
import { useSession } from '../features/identity/session';
import { useMeta } from '../features/system/useMeta';
import { setTheme, useThemePreference, type ThemePreference } from '../lib/theme';
import styles from './MorePage.module.css';
import { useAppNavigation } from './navigation';

const THEMES: ThemePreference[] = ['light', 'dark', 'system'];

/**
 * "Mehr" on phones and tablets (#80): the areas that are not in the tab bar,
 * grouped like the sidebar, and the account (iOS tab "Mehr"). Wide screens
 * have the sidebar instead and go to the overview.
 */
export function MorePage() {
  const { t } = useTranslation();
  const tabbar = useMediaQuery(tabbarQuery);
  const { rest } = useAppNavigation();
  const { me } = useSession();
  const meta = useMeta();
  const signOut = useSignOut();
  const theme = useThemePreference();

  if (!tabbar) return <Navigate to="/" replace />;

  const groups = new Map<string, SideNavItem[]>();
  for (const item of rest) {
    const key = item.section ?? '';
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }

  return (
    <Stack gap={6}>
      <PageHeader title={t('shell.more')} />
      {[...groups].map(([section, items]) => (
        <Section key={section} {...(section ? { title: section } : {})}>
          <List>
            {items.map((item) => (
              <ListRow
                key={item.key}
                {...(item.icon
                  ? {
                      leading: (
                        <span className={styles.icon}>
                          <Icon name={item.icon} size="md" />
                        </span>
                      ),
                    }
                  : {})}
                title={item.label}
                href={item.href}
              />
            ))}
          </List>
        </Section>
      ))}
      <Section title={t('more.account')}>
        <List>
          <ListRow
            title={<Persona name={me.name} secondary={me.email} />}
            {...(meta.data?.business_name ? { trailing: meta.data.business_name } : {})}
          />
          <ListRow title={t('userMenu.profile')} href="/einstellungen/profil" />
          <ListRow
            title={t('theme.label')}
            trailing={
              <SegmentedControl
                label={t('theme.label')}
                size="sm"
                value={theme}
                onChange={(value) => setTheme(value)}
                options={THEMES.map((value) => ({ value, label: t(`theme.${value}`) }))}
              />
            }
          />
          <ListRow title={t('userMenu.sessions')} href="/einstellungen/sitzungen" />
          <ListRow title={t('userMenu.logout')} tone="danger" onClick={signOut.start} />
        </List>
      </Section>
      {signOut.dialog}
    </Stack>
  );
}
